import React from "react";
import { useI18n } from "../i18n/I18nContext";
import { useSite } from "../context/SiteContext";
import PageHero from "../components/PageHero";
import { Database, Target, Clock, Share2, ShieldCheck, Cookie, UserCheck, Mail } from "lucide-react";

const CONTENT = {
  fr: {
    title: "Politique de confidentialité",
    subtitle: "Protection de vos données personnelles, conformément au RGPD et à la loi Informatique et Libertés.",
    crumb: "Politique de confidentialité",
    intro:
      "Le Domaine de la Bégaudière accorde une grande importance à la protection de votre vie privée. La présente politique décrit comment nous collectons, utilisons et protégeons vos données personnelles, dans le respect du Règlement Général sur la Protection des Données (RGPD - Règlement UE 2016/679) et de la loi française n° 78-17 du 6 janvier 1978 modifiée (« Informatique et Libertés »).",
    controllerTitle: "Responsable du traitement",
    controllerText: "Le responsable du traitement des données est :",
    sections: [
      {
        icon: "Database",
        title: "Données collectées",
        text: "Nous collectons uniquement les données nécessaires à nos services :",
        list: [
          "Identité et coordonnées : nom, prénom, adresse postale, e-mail, téléphone",
          "Données de réservation : dates de séjour, nombre de personnes, hébergement choisi, prestations optionnelles",
          "Données de paiement : traitées de manière sécurisée par notre prestataire Stripe (nous ne conservons aucune donnée bancaire)",
          "Fiche de police (voyageurs étrangers) : conformément à l'article R.611-42 du CESEDA",
          "Données de navigation : cookies techniques strictement nécessaires uniquement (voir ci-dessous)",
        ],
      },
      {
        icon: "Target",
        title: "Finalités et bases légales",
        text: "Vos données sont traitées pour les finalités suivantes :",
        list: [
          "Gestion des réservations et de la relation client (exécution du contrat)",
          "Facturation et obligations comptables et fiscales (obligation légale)",
          "Envoi de confirmations et d'informations liées à votre séjour (exécution du contrat)",
          "Réponse à vos demandes via les formulaires de contact et de devis (intérêt légitime / consentement)",
          "Amélioration du site (mesure d'audience non utilisée à ce jour)",
        ],
      },
      {
        icon: "Clock",
        title: "Durée de conservation",
        text: "Vos données sont conservées uniquement le temps nécessaire :",
        list: [
          "Données de réservation et de facturation : 10 ans (obligations comptables)",
          "Fiche de police : 6 mois (obligation légale)",
          "Prospects et demandes de contact : 3 ans après le dernier contact",
          "Cookies : 13 mois maximum",
        ],
      },
      {
        icon: "Share2",
        title: "Destinataires des données",
        text: "Vos données ne sont jamais vendues. Elles peuvent être communiquées à :",
        list: [
          "Nos prestataires techniques (paiement Stripe, envoi d'e-mails Resend, gestionnaire de réservations Beds24), agissant comme sous-traitants",
          "Les autorités administratives et judiciaires lorsque la loi l'exige",
        ],
      },
      {
        icon: "ShieldCheck",
        title: "Sécurité et transferts",
        text: "Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données (chiffrement, accès restreint). Certains sous-traitants peuvent traiter des données hors de l'Union européenne ; ces transferts sont alors encadrés par des garanties appropriées (clauses contractuelles types de la Commission européenne).",
        list: [],
      },
      {
        icon: "Cookie",
        title: "Cookies",
        text: "Le site utilise uniquement des cookies techniques strictement nécessaires à son bon fonctionnement (par exemple, mémorisation de vos préférences d'affichage). Aucun cookie de suivi publicitaire ni de mesure d'audience n'est déposé, et vos données de navigation ne sont ni collectées ni transmises à des tiers à des fins statistiques ou publicitaires.",
        list: [],
      },
    ],
    rightsTitle: "Vos droits",
    rightsText:
      "Conformément au RGPD et à la loi Informatique et Libertés, vous disposez des droits suivants sur vos données :",
    rights: [
      "Droit d'accès et de rectification",
      "Droit à l'effacement (« droit à l'oubli »)",
      "Droit à la limitation et à l'opposition au traitement",
      "Droit à la portabilité de vos données",
      "Droit de définir des directives relatives au sort de vos données après votre décès",
    ],
    rightsEnd:
      "Pour exercer ces droits, contactez-nous par e-mail ou courrier. Vous disposez également du droit d'introduire une réclamation auprès de la CNIL (Commission Nationale de l'Informatique et des Libertés, www.cnil.fr).",
    contactTitle: "Nous contacter",
    updated: "Dernière mise à jour : juillet 2026.",
  },
  en: {
    title: "Privacy policy",
    subtitle: "Protection of your personal data, in accordance with the GDPR and French data protection law.",
    crumb: "Privacy policy",
    intro:
      "Domaine de la Bégaudière places great importance on protecting your privacy. This policy describes how we collect, use and protect your personal data, in compliance with the General Data Protection Regulation (GDPR - EU Regulation 2016/679) and French Law No. 78-17 of 6 January 1978 as amended (\"Informatique et Libertés\").",
    controllerTitle: "Data controller",
    controllerText: "The data controller is:",
    sections: [
      {
        icon: "Database",
        title: "Data collected",
        text: "We only collect data necessary for our services:",
        list: [
          "Identity and contact details: first name, last name, postal address, e-mail, phone",
          "Booking data: stay dates, number of guests, chosen accommodation, optional services",
          "Payment data: securely processed by our provider Stripe (we do not store any banking data)",
          "Police form (foreign travellers): in accordance with Article R.611-42 of the CESEDA",
          "Browsing data: strictly necessary technical cookies only (see below)",
        ],
      },
      {
        icon: "Target",
        title: "Purposes and legal bases",
        text: "Your data is processed for the following purposes:",
        list: [
          "Managing bookings and customer relations (performance of the contract)",
          "Invoicing and accounting/tax obligations (legal obligation)",
          "Sending confirmations and information about your stay (performance of the contract)",
          "Responding to your requests via contact and quote forms (legitimate interest / consent)",
          "Improving the website (audience measurement not used to date)",
        ],
      },
      {
        icon: "Clock",
        title: "Retention period",
        text: "Your data is kept only as long as necessary:",
        list: [
          "Booking and invoicing data: 10 years (accounting obligations)",
          "Police form: 6 months (legal obligation)",
          "Prospects and contact requests: 3 years after the last contact",
          "Cookies: 13 months maximum",
        ],
      },
      {
        icon: "Share2",
        title: "Data recipients",
        text: "Your data is never sold. It may be shared with:",
        list: [
          "Our technical providers (Stripe payments, Resend e-mails, Beds24 booking manager), acting as processors",
          "Administrative and judicial authorities when required by law",
        ],
      },
      {
        icon: "ShieldCheck",
        title: "Security and transfers",
        text: "We implement appropriate technical and organisational measures to protect your data (encryption, restricted access). Some processors may handle data outside the European Union; such transfers are then governed by appropriate safeguards (European Commission standard contractual clauses).",
        list: [],
      },
      {
        icon: "Cookie",
        title: "Cookies",
        text: "The site only uses technical cookies strictly necessary for its proper functioning (for example, remembering your display preferences). No advertising or audience-measurement tracking cookies are placed, and your browsing data is neither collected nor shared with third parties for statistical or advertising purposes.",
        list: [],
      },
    ],
    rightsTitle: "Your rights",
    rightsText: "In accordance with the GDPR and French data protection law, you have the following rights over your data:",
    rights: [
      "Right of access and rectification",
      "Right to erasure (\"right to be forgotten\")",
      "Right to restriction of and objection to processing",
      "Right to data portability",
      "Right to set directives regarding your data after your death",
    ],
    rightsEnd:
      "To exercise these rights, contact us by e-mail or post. You also have the right to lodge a complaint with the CNIL (French data protection authority, www.cnil.fr).",
    contactTitle: "Contact us",
    updated: "Last updated: July 2026.",
  },
};

const ICONS = { Database, Target, Clock, Share2, ShieldCheck, Cookie };

export default function PrivacyPage() {
  const { lang } = useI18n();
  const { settings } = useSite();
  const c = CONTENT[lang] || CONTENT.fr;
  const email = settings.email || "contact@labegaudiere.fr";
  const address = settings.address || "12 La Bégaudière, 35120 Mont-Dol";

  return (
    <div data-testid="privacy-page">
      <PageHero title={c.title} subtitle={c.subtitle} image={settings.gallery?.[3] || settings.hero_image} crumb={c.crumb} />
      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <p className="font-body text-ink-600 leading-relaxed text-base">{c.intro}</p>

          {/* Responsable du traitement */}
          <div className="mt-8 rounded-2xl border border-brand/20 bg-brand-50/40 p-6">
            <h2 className="font-heading text-lg text-ink-800">{c.controllerTitle}</h2>
            <p className="mt-2 font-body text-ink-600 text-sm">{c.controllerText}</p>
            <p className="mt-2 font-body text-ink-700 text-sm leading-relaxed">
              <strong>Domaine de la Bégaudière</strong><br />
              {address}<br />
              <a href={`mailto:${email}`} className="text-brand underline underline-offset-2">{email}</a>
              {settings.siret && <><br />SIRET : {settings.siret}</>}
            </p>
          </div>

          <div className="mt-6 space-y-6">
            {c.sections.map((s, i) => {
              const Icon = ICONS[s.icon];
              return (
                <div key={i} className="rounded-2xl border border-black/5 bg-white p-6 shadow-sm" data-testid={`privacy-section-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-50 text-brand"><Icon size={20} /></span>
                    <h2 className="font-heading text-xl text-ink-800">{s.title}</h2>
                  </div>
                  <p className="mt-4 font-body text-ink-600 leading-relaxed text-sm">{s.text}</p>
                  {s.list.length > 0 && (
                    <ul className="mt-3 space-y-1.5">
                      {s.list.map((x, j) => (
                        <li key={j} className="flex items-start gap-2 font-body text-ink-700 text-sm">
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />{x}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>

          {/* Vos droits */}
          <div className="mt-6 rounded-2xl border border-black/5 bg-white p-6 shadow-sm" data-testid="privacy-rights">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-50 text-brand"><UserCheck size={20} /></span>
              <h2 className="font-heading text-xl text-ink-800">{c.rightsTitle}</h2>
            </div>
            <p className="mt-4 font-body text-ink-600 text-sm">{c.rightsText}</p>
            <ul className="mt-3 space-y-1.5">
              {c.rights.map((x, i) => (
                <li key={i} className="flex items-start gap-2 font-body text-ink-700 text-sm">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />{x}
                </li>
              ))}
            </ul>
            <p className="mt-3 font-body text-ink-500 text-sm leading-relaxed">{c.rightsEnd}</p>
          </div>

          <p className="mt-8 flex items-center gap-2 font-body text-ink-500 text-sm">
            <Mail size={15} className="text-brand" /> {c.contactTitle} :{" "}
            <a href={`mailto:${email}`} className="text-brand underline underline-offset-2" data-testid="privacy-contact-email">{email}</a>
          </p>
          <p className="mt-3 font-body text-ink-400 text-xs">{c.updated}</p>
        </div>
      </section>
    </div>
  );
}
